import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { industryOptions } from "../../shared/data/sharedText";
import { createCalComUrl } from "../utils/companyUtils";
import i18n from "../../utils/i18n";

// this is how our form are integrated in HubSpot landing pages

// LOCAL
{
  /* <div style="max-width: 560px; margin: 0 auto; width: 100%;"><iframe id="hubspotFormIframe" style="border: 0; display: block; margin: 0px auto;" title="Konyvelo form" xml="lang" src="https://x2d6p5hs-8081.euw.devtunnels.ms/company/hubspotform?flow=HU-ASP-MIC-LOCAL/TEST/MRR/10000" width="560" height="315" loading="eager"></iframe></div>
<script>
  var allowedOrigin = "https://x2d6p5hs-8081.euw.devtunnels.ms";

  window.addEventListener("message", function (event) {
    if (!event || event.origin !== allowedOrigin) {
      return;
    }
    if (!event.data || event.data.type !== "hubspotFormHeight") {
      return;
    }
    var iframe = document.getElementById("hubspotFormIframe");
    if (!iframe) {
      return;
    }
    var nextHeight = Number(event.data.height);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      return;
    }
    iframe.style.height = nextHeight + "px";
  });
</script> */
}

// DEVELOPMENT
{
  /* <div style="max-width: 560px; margin: 0 auto; width: 100%;"><iframe id="hubspotFormIframe" style="border: 0; display: block; margin: 0px auto;" title="Konyvelo form" xml="lang" src="https://dev-hu.bookkeepie.com/company/hubspotform?flow=HU-ASP-GEN-DEV/TEST/EV-MINI/MRR/S/10000" width="560" height="315" loading="eager"></iframe></div>
<script>
  var allowedOrigin = "https://dev-hu.bookkeepie.com";

  window.addEventListener("message", function (event) {
    if (!event || event.origin !== allowedOrigin) {
      return;
    }
    if (!event.data || event.data.type !== "hubspotFormHeight") {
      return;
    }
    var iframe = document.getElementById("hubspotFormIframe");
    if (!iframe) {
      return;
    }
    var nextHeight = Number(event.data.height);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      return;
    }
    iframe.style.height = nextHeight + "px";
  });
</script> */
}

// PRODUCTION
{
  /* <div style="max-width: 100%; margin: 0 auto; width: 100%; height: 100%;"><iframe id="hubspotFormIframe" style="border: 0; display: block; margin: 0 auto; max-width: 100%;" title="Konyvelo form" xml="lang" src="https://dashboard-hu.bookkeepie.com/company/hubspotform?flow=HU-ASP-GEN-PROD/KFT-MINI/MRR/S/34500" width="560" height="315" loading="eager"></iframe></div>
<script>
  var allowedOrigin = "https://dashboard-hu.bookkeepie.com/";

  window.addEventListener("message", function (event) {
    if (!event || event.origin !== allowedOrigin) {
      return;
    }
    if (!event.data || event.data.type !== "hubspotFormHeight") {
      return;
    }
    var iframe = document.getElementById("hubspotFormIframe");
    if (!iframe) {
      return;
    }
    var nextHeight = Number(event.data.height);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      return;
    }
    iframe.style.height = nextHeight + "px";
  });
</script> */
}

// how product names are named in the flow

// HU-ASP-GEN-PROD/KATA-START/MRR/S/11500 =
// {COUNTRY}-{LEADTYPE}-{FLOW}-{ENV}/{PACKAGE}/{BILLING}/{VAT_SEGMENT}/{PRICE}

// HU: country
// ASP: lead/service type
// GEN: flow family
// PROD: environment
// KATA-START: package tier
// MRR: monthly recurring billing
// S: VAT segment flag
// 11500: price (typically HUF/month)

// If the vatFilter flow is provided dynamically by the user, the VAT segment flag is omitted.
// HU-ASP-GEN-PROD/EV-MED/MRR/20750

const backendBaseUrl =
  process.env.REACT_APP_BACKEND_API || "http://localhost:5000";

const updateUrlWithEmail = (email) => {
  if (!email) {
    return;
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set("email", email);
    window.history.replaceState(null, "", url.toString());
  } catch (error) {
    console.warn("Failed to update URL with email", error);
  }
};

const getFlowFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get("flow") || "";
  } catch (error) {
    console.warn("Failed to read flow from URL", error);
    return "";
  }
};

const getVatFilterFromFlow = (flowKey) => {
  if (!flowKey) {
    return null;
  }
  const segments = flowKey.split("/").filter(Boolean);
  if (segments.length < 2) {
    return null;
  }
  const vatSegment = segments[segments.length - 2];
  if (vatSegment === "A") {
    return true;
  }
  if (vatSegment === "S") {
    return false;
  }
  return null;
};

const normalizeFlowKey = (flowKey) => {
  if (!flowKey) {
    return "";
  }
  const segments = flowKey.split("/").filter(Boolean);
  if (segments.length < 2) {
    return flowKey;
  }
  const vatSegment = segments[segments.length - 2];
  if (vatSegment === "A" || vatSegment === "S") {
    segments.splice(segments.length - 2, 1);
  }
  return segments.join("/");
};

const isEvFlow = (flowKey) => {
  if (!flowKey) {
    return false;
  }
  return flowKey
    .split("/")
    .filter(Boolean)
    .some((segment) => segment === "EV" || segment.startsWith("EV-"));
};

const isGenFlow = (flowKey) => {
  if (!flowKey) {
    return false;
  }
  return flowKey
    .split("/")
    .filter(Boolean)
    .some((segment) => segment.includes("GEN"));
};

const applyVatSegment = (flowKey, vatFilterValue) => {
  if (!flowKey || typeof vatFilterValue !== "boolean") {
    return flowKey;
  }
  const segments = flowKey.split("/").filter(Boolean);
  if (segments.length < 2) {
    return flowKey;
  }
  const priceSegment = segments.pop();
  const vatSegment = vatFilterValue ? "A" : "S";
  return [...segments, vatSegment, priceSegment].join("/");
};

const TAX_NUMBER_PATTERN = /^\d{8}-\d-\d{2}$/;

const ONECLICK_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const isValidTaxNumber = (value) => TAX_NUMBER_PATTERN.test(value.trim());

const isValidOneClickEmail = (value) =>
  ONECLICK_EMAIL_PATTERN.test((value || "").trim());

const formatTaxNumber = (value) => {
  const digitsOnly = value.replace(/\D/g, "").slice(0, 11);
  const part1 = digitsOnly.slice(0, 8);
  const part2 = digitsOnly.slice(8, 9);
  const part3 = digitsOnly.slice(9, 11);
  let formatted = part1;
  if (part2) {
    formatted += `-${part2}`;
  }
  if (part3) {
    formatted += `-${part3}`;
  }
  return formatted;
};

const sendEmbedHeight = () => {
  if (typeof window === "undefined" || window.parent === window) {
    return;
  }
  const height =
    document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;
  window.parent.postMessage({ type: "hubspotFormHeight", height }, "*");
};

const HubSpotForm = () => {
  const flowFromUrl = getFlowFromUrl() || "testRequest";
  const templateVatFilter = getVatFilterFromFlow(flowFromUrl);
  const [company, setCompany] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [industry, setIndustry] = useState("");
  const [industryMenuWidth, setIndustryMenuWidth] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [vatFilter, setVatFilter] = useState(templateVatFilter);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [powerOfAttorneyAccepted, setPowerOfAttorneyAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [formError, setFormError] = useState("");
  const [calcomUrlRedirect, setCalcomUrlRedirect] = useState("");
  const [calcomFieldErrors, setCalcomFieldErrors] = useState({
    lastName: false,
    firstName: false,
    email: false,
    phone: false,
  });
  const [emailInputError, setEmailInputError] = useState(false);
  const industryFieldRef = useRef(null);
  const calcomLastNameRef = useRef(null);
  const calcomFirstNameRef = useRef(null);
  const calcomEmailRef = useRef(null);
  const calcomPhoneRef = useRef(null);

  // Only hide name fields for EV flows.
  const isEvFlowKey = isEvFlow(flowFromUrl);
  const isGenFlowKey = isGenFlow(flowFromUrl);

  const industryOptionsList = industryOptions();

  useEffect(() => {
    if (templateVatFilter !== null && vatFilter !== templateVatFilter) {
      setVatFilter(templateVatFilter);
    }
  }, [templateVatFilter, vatFilter]);

  useEffect(() => {
    if (!flowFromUrl) {
      return;
    }

    const controller = new AbortController();
    const loadFlowTemplate = async () => {
      try {
        const response = await fetch(
          `${backendBaseUrl}/api/generate-stripe-redirect/template?flow=${encodeURIComponent(
            flowFromUrl,
          )}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (typeof data?.calcomUrlRedirect === "string") {
          setCalcomUrlRedirect(data.calcomUrlRedirect);
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.warn("Failed to load oneclick flow template", error);
        }
      }
    };

    loadFlowTemplate();
    return () => controller.abort();
  }, [flowFromUrl]);

  useEffect(() => {
    sendEmbedHeight();
  }, [calcomUrlRedirect]);

  useEffect(() => {
    sendEmbedHeight();

    const handleResize = () => sendEmbedHeight();
    window.addEventListener("resize", handleResize);

    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => sendEmbedHeight());
      observer.observe(document.body);
    }

    const timeoutId = window.setTimeout(() => sendEmbedHeight(), 300);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (observer) {
        observer.disconnect();
      }
      window.clearTimeout(timeoutId);
    };
  }, []);

  const getRequiredAsteriskColor = (value) =>
    value?.trim() ? "#111" : "#d32f2f";

  const clearCalcomFieldError = (field) => {
    if (field === "email") {
      setEmailInputError(false);
    }
    setCalcomFieldErrors((prev) =>
      prev[field] ? { ...prev, [field]: false } : prev,
    );
  };

  const scrollToCalcomField = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const calcomFieldBorderSx = (hasError) =>
    hasError
      ? {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d32f2f",
            borderWidth: "1px",
          },
        }
      : undefined;

  const navigateToPayment = (url) => {
    try {
      const topResult = window.open(url, "_top");
      if (topResult) {
        return;
      }
      const parentResult = window.open(url, "_parent");
      if (parentResult) {
        return;
      }
    } catch (error) {
      // Cross-origin access can throw; fall back to current window.
      console.warn("Top/parent navigation failed", error);
    }
    window.location.assign(url);
  };

  const navigateWithNoReferrer = (url) => {
    try {
      const topWindow = window.top || window;
      const link = topWindow.document.createElement("a");
      link.href = url;
      link.rel = "noreferrer";
      link.target = "_top";
      topWindow.document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    } catch (error) {
      console.warn("Noreferrer navigation failed", error);
    }
    window.open(url, "_top", "noopener,noreferrer");
  };

  const handleCalcomClick = async () => {
    setErrorMessage("");
    setFormError("");
    setCalcomFieldErrors({
      lastName: false,
      firstName: false,
      email: false,
      phone: false,
    });
    setEmailInputError(false);

    const trimmedEmail = email.trim();
    const trimmedLastName = lastName.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedPhone = (phone || "").replace(/\D/g, "");
    const phoneWithCode = trimmedPhone ? `+${trimmedPhone}` : "";
    const companyctcname = `${trimmedLastName} ${trimmedFirstName}`.trim();

    const nextCalcomFieldErrors = {
      lastName: !trimmedLastName,
      firstName: !trimmedFirstName,
      email: !trimmedEmail || !isValidOneClickEmail(trimmedEmail),
      phone: !trimmedPhone,
    };

    if (Object.values(nextCalcomFieldErrors).some(Boolean)) {
      setCalcomFieldErrors(nextCalcomFieldErrors);
      setEmailInputError(nextCalcomFieldErrors.email);
      setFormError(
        trimmedEmail && !isValidOneClickEmail(trimmedEmail)
          ? i18n.t("oneClick.invalidEmail")
          : i18n.t("oneClick.calcomRequiredFields"),
      );
      const firstInvalidRef = [
        [nextCalcomFieldErrors.lastName, calcomLastNameRef],
        [nextCalcomFieldErrors.firstName, calcomFirstNameRef],
        [nextCalcomFieldErrors.email, calcomEmailRef],
        [nextCalcomFieldErrors.phone, calcomPhoneRef],
      ].find(([invalid]) => invalid)?.[1];
      if (firstInvalidRef) {
        scrollToCalcomField(firstInvalidRef);
      }
      return;
    }

    setStatus("calcomSubmitting");

    const normalizedFlowKey = normalizeFlowKey(flowFromUrl);
    const effectiveVatFilter =
      templateVatFilter !== null ? templateVatFilter : vatFilter;
    const flowKey = applyVatSegment(normalizedFlowKey, effectiveVatFilter);

    updateUrlWithEmail(trimmedEmail);

    try {
      const response = await fetch(
        `${backendBaseUrl}/api/generate-stripe-redirect/calcom-inquiry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmedEmail,
            companyctcname,
            companylegalname: companyctcname,
            phone: phoneWithCode,
            flow: flowKey,
            elfogadja_a_marketinget: marketingAccepted,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Backend request failed");
      }

      const data = await response.json();
      const reqId = data?.result?._id || data?.reqId;
      if (!reqId) {
        throw new Error("Missing reqId in backend response");
      }

      const calcomUrl = createCalComUrl({
        reqId,
        compCtcName: companyctcname,
        compEmail: trimmedEmail,
        compPhone: phoneWithCode,
        baseUrl: calcomUrlRedirect,
      });
      if (!calcomUrl) {
        throw new Error("Failed to build Cal.com URL");
      }
      navigateWithNoReferrer(calcomUrl);
    } catch (error) {
      console.error("Failed to create calcom inquiry AspRequest", error);
      setStatus("idle");
      setErrorMessage(i18n.t("oneClick.redirectFailed"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("submitting");
    setErrorMessage("");
    setFormError("");
    setEmailInputError(false);
    setCalcomFieldErrors((prev) => ({ ...prev, email: false }));

    const trimmedEmail = email.trim();
    const trimmedCompany = company.trim();
    const trimmedTaxNumber = taxNumber.trim();
    const trimmedLastName = lastName.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedPhone = (phone || "").replace(/\D/g, "");
    const phoneWithCode = trimmedPhone ? `+${trimmedPhone}` : "";
    const companyctcname = `${trimmedLastName} ${trimmedFirstName}`.trim();
    const evCompanyName = companyctcname
      ? `${companyctcname} ${i18n.t("oneClick.entrepreneur")}`
      : "";

    // Use the URL flow as the base and normalize to remove any A/S marker
    // before the price. If the flow includes /A/ or /S/, we hide the VAT
    // question and force the value; otherwise we show the question.
    const normalizedFlowKey = normalizeFlowKey(flowFromUrl);
    const effectiveVatFilter =
      templateVatFilter !== null ? templateVatFilter : vatFilter;
    const flowKey = applyVatSegment(normalizedFlowKey, effectiveVatFilter);

    const backendPayload = {
      companylegalname: isEvFlowKey ? evCompanyName : trimmedCompany, // companylegalname
      email: trimmedEmail, // email
      companyctcname, // companyctcname
      vatFilter,
      hu_adoszam: trimmedTaxNumber, // companytaxnumber
      phone: phoneWithCode, //companyPhone
      terms_accepted: termsAccepted, // gdpr
      elfogadja_a_marketinget: marketingAccepted, // marketing
      flow: flowKey,
    };

    if (isGenFlowKey) {
      backendPayload.industry = industry;
    }

    if (!isValidTaxNumber(trimmedTaxNumber)) {
      setStatus("idle");
      setFormError(
        i18n.t("oneClick.taxNumberFormatPrefix") +
        " " +
        i18n.t("oneClick.taxNumberPlaceholder"),
      );
      return;
    }

    if (trimmedEmail && !isValidOneClickEmail(trimmedEmail)) {
      setStatus("idle");
      setEmailInputError(true);
      setCalcomFieldErrors((prev) => ({ ...prev, email: true }));
      setFormError(i18n.t("oneClick.invalidEmail"));
      scrollToCalcomField(calcomEmailRef);
      return;
    }

    if (
      (!isEvFlowKey && !trimmedCompany) ||
      !trimmedTaxNumber ||
      !trimmedLastName ||
      !trimmedFirstName ||
      (isGenFlowKey && !industry) ||
      !trimmedEmail ||
      !trimmedPhone ||
      (templateVatFilter === null && vatFilter === null) ||
      !termsAccepted ||
      !powerOfAttorneyAccepted
    ) {
      setStatus("idle");
      setFormError(i18n.t("oneClick.requiredFields"));
      return;
    }

    updateUrlWithEmail(trimmedEmail);

    try {
      const response = await fetch(
        `${backendBaseUrl}/api/generate-stripe-redirect`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(backendPayload),
        },
      );

      if (!response.ok) {
        throw new Error("Backend request failed");
      }

      const data = await response.json();
      const reqId = data?.result?._id || data?.reqId;
      if (!reqId) {
        throw new Error("Missing reqId in backend response");
      }

      console.log("Stripe flow reqId:", reqId);

      const nextUrl = `${backendBaseUrl}/api/hubspot-redirect/submission?reqId=${encodeURIComponent(
        reqId,
      )}`;
      navigateToPayment(nextUrl);
    } catch (error) {
      console.error("Failed to redirect", error);
      setStatus("error");
      setErrorMessage(i18n.t("oneClick.redirectFailed"));
    }
  };

  return (
    <>
      {/*
        SEO protection for the HubSpot form route used inside an iframe:
        - noindex/nofollow/noarchive meta tags: search engines should not index or follow it
        - robots.txt also explicitly disallows the /company/hubspotform path
      */}
      <MetaTags>
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noarchive" />
      </MetaTags>
      <Box
        sx={{
          width: "auto",
          maxWidth: 560,
          mx: "auto",
          my: { xs: 2, md: 3 },
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 3 },
          borderRadius: 3,
          border: "1px solid #e5e7f0",
          bgcolor: "#fff",
          boxShadow: "0 8px 24px rgba(9, 30, 66, 0.08)",
          fontFamily: "Satoshi, sans-serif",
          "& .MuiTypography-root": {
            fontFamily: "Satoshi, sans-serif",
            fontSize: "1rem",
          },
          "& .MuiInputBase-input": {
            fontFamily: "Satoshi, sans-serif",
            fontSize: "1rem",
          },
          "& .MuiButton-root": {
            fontFamily: "Satoshi, sans-serif",
            fontSize: "1rem",
          },
        }}
      >
        {!isEvFlowKey && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.6 }}>
              {i18n.t("general.companyname")}
              <Box
                component="span"
                sx={{ color: getRequiredAsteriskColor(company) }}
              >
                *
              </Box>
            </Typography>
            <Typography
              sx={{
                color: "#5b6477",
                mb: 1.2,
                "&&": { fontSize: "0.9rem" },
              }}
            >
              {i18n.t("oneClick.companyHelp")}
            </Typography>
          </>
        )}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {!isEvFlowKey && (
            <TextField
              fullWidth
              name="company"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              variant="outlined"
              InputProps={{
                sx: {
                  bgcolor: "#f3f4fb",
                  borderRadius: 2,
                },
              }}
              sx={{ mb: 2 }}
            />
          )}

          <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.6 }}>
            {i18n.t("general.taxnumber")}
            <Box
              component="span"
              sx={{ color: getRequiredAsteriskColor(taxNumber) }}
            >
              *
            </Box>
          </Typography>
          <Typography
            sx={{ color: "#5b6477", mb: 1.2, "&&": { fontSize: "0.9rem" } }}
          >
            {i18n.t("oneClick.taxNumberHelpPrefix")}
            <Box
              component="span"
              sx={{
                fontWeight: 700,
                color: "#4d5bec",
                "&&": { fontSize: "0.9rem" },
              }}
            >
              {i18n.t("oneClick.taxNumberHelpBold")}
            </Box>
            {i18n.t("oneClick.taxNumberHelpSuffix")}
          </Typography>
          <TextField
            fullWidth
            name="hu_adoszam"
            value={taxNumber}
            onChange={(event) =>
              setTaxNumber(formatTaxNumber(event.target.value))
            }
            placeholder={i18n.t("oneClick.taxNumberPlaceholder")}
            variant="outlined"
            inputProps={{
              pattern: "\\d{8}-\\d-\\d{2}",
              maxLength: 13,
              inputMode: "numeric",
            }}
            InputProps={{
              sx: {
                bgcolor: "#f3f4fb",
                borderRadius: 2,
              },
            }}
            sx={{ mb: 2 }}
          />

          {templateVatFilter === null && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("oneClick.vatTitle")}
                <Box component="span" sx={{ color: "#d32f2f" }}>
                  *
                </Box>
              </Typography>
              <Typography
                sx={{ color: "#5b6477", mb: 1.2, "&&": { fontSize: "0.9rem" } }}
              >
                {i18n.t("oneClick.vatHelp")}
              </Typography>
              <ToggleButtonGroup
                fullWidth
                exclusive
                value={vatFilter}
                onChange={(_event, nextValue) => {
                  if (nextValue === null) {
                    return;
                  }
                  setVatFilter(nextValue);
                }}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid #d7dbea",
                  py: 0.4,
                  columnGap: 0.5,
                  "& .MuiToggleButtonGroup-grouped": {
                    flex: 1,
                    border: "none",
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    py: 1.1,
                    m: 0,
                    mx: 0.25,
                  },
                  "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                    borderRadius: 2,
                  },
                  "& .MuiToggleButtonGroup-grouped:not(:last-of-type)": {
                    borderRadius: 2,
                  },
                  "& .MuiToggleButton-root": {
                    color: "#3a4256",
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 2,
                    fontFamily: "Satoshi, sans-serif",
                    fontSize: "0.9rem",
                  },
                  "& .MuiToggleButton-root:hover": {
                    borderRadius: 2,
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: "#1C32F3",
                    color: "#fff",
                    borderRadius: 2,
                  },
                  "& .MuiToggleButton-root.Mui-selected:hover": {
                    bgcolor: "#1C32F3",
                    borderRadius: 2,
                  },
                }}
              >
                <ToggleButton value={true}>
                  {i18n.t("oneClick.vatYes")}
                </ToggleButton>
                <ToggleButton value={false}>
                  {i18n.t("oneClick.vatNo")}
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}

          {isGenFlowKey && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("oneClick.industryQuestion")}
                <Box
                  component="span"
                  sx={{ color: getRequiredAsteriskColor(industry) }}
                >
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                select
                name="industry"
                value={industry}
                inputRef={industryFieldRef}
                onChange={(event) => setIndustry(event.target.value)}
                variant="outlined"
                SelectProps={{
                  autoWidth: true,
                  onOpen: () => {
                    const width = industryFieldRef.current?.offsetWidth;
                    if (width) {
                      setIndustryMenuWidth(Math.round(width / 2));
                    }
                  },
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        width: industryMenuWidth
                          ? `${industryMenuWidth}px`
                          : undefined,
                        maxWidth: industryMenuWidth
                          ? `${industryMenuWidth}px`
                          : undefined,
                        minWidth: industryMenuWidth
                          ? `${industryMenuWidth}px`
                          : undefined,
                      },
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    bgcolor: "#f3f4fb",
                    borderRadius: "8px",
                  },
                }}
                sx={{ mb: 2 }}
              >
                {industryOptionsList.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("oneClick.lastNameLabel")}
                <Box
                  component="span"
                  sx={{ color: getRequiredAsteriskColor(lastName) }}
                >
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                name="lastname"
                inputRef={calcomLastNameRef}
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                  clearCalcomFieldError("lastName");
                }}
                placeholder=""
                variant="outlined"
                error={calcomFieldErrors.lastName}
                sx={calcomFieldBorderSx(calcomFieldErrors.lastName)}
                InputProps={{
                  sx: {
                    bgcolor: "#f3f4fb",
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("oneClick.firstNameLabel")}
                <Box
                  component="span"
                  sx={{ color: getRequiredAsteriskColor(firstName) }}
                >
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                name="firstname"
                inputRef={calcomFirstNameRef}
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                  clearCalcomFieldError("firstName");
                }}
                placeholder=""
                variant="outlined"
                error={calcomFieldErrors.firstName}
                sx={calcomFieldBorderSx(calcomFieldErrors.firstName)}
                InputProps={{
                  sx: {
                    bgcolor: "#f3f4fb",
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("oneClick.emailLabel")}
                <Box
                  component="span"
                  sx={{ color: getRequiredAsteriskColor(email) }}
                >
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                type="email"
                name="email"
                inputRef={calcomEmailRef}
                value={email}
                onChange={(event) => {
                  const nextEmail = event.target.value;
                  setEmail(nextEmail);
                  updateUrlWithEmail(nextEmail.trim());
                  clearCalcomFieldError("email");
                }}
                placeholder=""
                variant="outlined"
                error={calcomFieldErrors.email || emailInputError}
                sx={calcomFieldBorderSx(calcomFieldErrors.email || emailInputError)}
                InputProps={{
                  sx: {
                    bgcolor: "#f3f4fb",
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: 500, mb: 0.6 }}>
                {i18n.t("general.phone")}
                <Box
                  component="span"
                  sx={{ color: getRequiredAsteriskColor(phone) }}
                >
                  *
                </Box>
              </Typography>
              <Box
                ref={calcomPhoneRef}
                sx={{
                  "& .react-tel-input": {
                    borderRadius: "8px",
                  },
                  ...(calcomFieldErrors.phone
                    ? {
                        "& .react-tel-input .form-control, & .react-tel-input .flag-dropdown":
                          {
                            borderColor: "#d32f2f !important",
                            borderWidth: "1px",
                          },
                      }
                    : {}),
                  "& .react-tel-input .flag-dropdown": {
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                    backgroundColor: "#ffffff",
                  },
                  "& .react-tel-input .selected-flag": {
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  },
                  "& .react-tel-input .selected-flag, & .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag:focus, & .react-tel-input .flag-dropdown:hover, & .react-tel-input .flag-dropdown:focus, & .react-tel-input .flag-dropdown.open":
                  {
                    backgroundColor: "#ffffff !important",
                  },
                  "& .react-tel-input .flag-dropdown.open": {
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                    borderColor: "#c4c9d6",
                    backgroundColor: "#ffffff !important",
                  },
                  "& .react-tel-input .flag-dropdown.open .selected-flag": {
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                    backgroundColor: "#ffffff !important",
                  },
                  "& .react-tel-input .country-list": {
                    zIndex: 1300,
                  },
                }}
              >
                <PhoneInput
                  country="hu"
                  enableSearch
                  searchPlaceholder={i18n.t("company.search")}
                  searchNotFound={i18n.t("searchflow.nomatchfound")}
                  countryCodeEditable={false}
                  value={phone}
                  onChange={(value) => {
                    setPhone(value || "");
                    clearCalcomFieldError("phone");
                  }}
                  inputProps={{
                    name: "phone",
                    required: true,
                  }}
                  specialLabel={""}
                  inputStyle={{
                    width: "100%",
                    height: 56,
                    backgroundColor: "#ffffff",
                    borderRadius: 8,
                    borderColor: calcomFieldErrors.phone ? "#d32f2f" : "#c4c9d6",
                    borderWidth: "1px",
                    fontFamily: "Satoshi, sans-serif",
                    fontSize: "1rem",
                  }}
                  buttonStyle={{
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    backgroundColor: "#ffffff",
                    borderColor: calcomFieldErrors.phone ? "#d32f2f" : "#c4c9d6",
                    borderWidth: "1px",
                  }}
                  dropdownStyle={{
                    fontFamily: "Satoshi, sans-serif",
                  }}
                  searchStyle={{
                    fontFamily: "Satoshi, sans-serif",
                    fontSize: "0.95rem",
                    borderRadius: 6,
                    padding: "8px 10px",
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box>
            <FormControlLabel
              sx={{ alignItems: "center" }}
              control={
                <Checkbox
                  name="power_of_attorney_accepted"
                  checked={powerOfAttorneyAccepted}
                  onChange={(event) =>
                    setPowerOfAttorneyAccepted(event.target.checked)
                  }
                  inputProps={{ required: true }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Satoshi, sans-serif",
                    color: "#3a4256",
                    lineHeight: 1.35,
                    "&&": { fontSize: "0.9rem" },
                  }}
                >
                  {i18n.t("oneClick.powerOfAttorneyText")}{" "}
                  <Link
                    href="https://drive.google.com/file/d/12Lf3mToQpAjm3o0RjSF19OWFcK3LutRX/view"
                    underline="always"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ "&&": { fontSize: "0.9rem", color: "#4d5bec", fontWeight: 600 } }}
                  >
                    {i18n.t("oneClick.powerOfAttorney")}
                  </Link>
                  {i18n.t("oneClick.powerOfAttorneySuffix")}
                </Typography>
              }
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <FormControlLabel
              sx={{ alignItems: "center" }}
              control={
                <Checkbox
                  name="terms_accepted"
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                  inputProps={{ required: true }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Satoshi, sans-serif",
                    color: "#3a4256",
                    lineHeight: 1.35,
                    "&&": { fontSize: "0.9rem" },
                  }}
                >
                  {i18n.t("oneClick.termsPrefix")}{" "}
                  <Link
                    href="https://drive.google.com/file/d/1vPD0t4Jl1WeifR4QLcmiNeBUh_pxX8_1/view"
                    underline="always"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ "&&": { fontSize: "0.9rem", color: "#4d5bec", fontWeight: 600 } }}
                  >
                    {i18n.t("oneClick.termsPrivacy")}
                  </Link>{" "}
                  {i18n.t("oneClick.termsAnd")}{" "}
                  <Link
                    href="https://drive.google.com/file/d/1AHgVYNzt6z0v5wLyuL7KLTgMsyyUU3za/view"
                    underline="always"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ "&&": { fontSize: "0.9rem", color: "#4d5bec", fontWeight: 600 } }}
                  >
                    {i18n.t("oneClick.termsTerms")}
                  </Link>{" "}
                  {i18n.t("oneClick.termsSuffix")}
                </Typography>
              }
            />
          </Box>
          <Box sx={{ mb: 3.5 }}>
            <FormControlLabel
              sx={{ alignItems: "center" }}
              control={
                <Checkbox
                  name="elfogadja_a_marketinget"
                  checked={marketingAccepted}
                  onChange={(event) =>
                    setMarketingAccepted(event.target.checked)
                  }
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Satoshi, sans-serif",
                    color: "#3a4256",
                    lineHeight: 1.35,
                    "&&": { fontSize: "0.9rem" },
                  }}
                >
                  {i18n.t("oneClick.marketingText")}
                </Typography>
              }
            />
          </Box>

          {formError && (
            <Typography
              sx={{
                color: "#c00",
                fontWeight: 500,
                textAlign: "center",
                px: 2,
                pb: 1.5,
              }}
            >
              {formError}
            </Typography>
          )}
          <Button
            type="submit"
            fullWidth
            disabled={status === "submitting" || status === "calcomSubmitting"}
            sx={{
              height: "44px",
              bgcolor: "#fdb400",
              color: "#333c45",
              fontWeight: 700,
              letterSpacing: "normal",
              py: 1.3,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { bgcolor: "#f5ca61" },
            }}
          >
            {status === "submitting"
              ? i18n.t("oneClick.submitLoading")
              : i18n.t("oneClick.submitCta")}
          </Button>
          {calcomUrlRedirect && (
            <Button
              type="button"
              fullWidth
              disabled={status === "submitting" || status === "calcomSubmitting"}
              onClick={handleCalcomClick}
              sx={{
                mt: 1.5,
                height: "44px",
                bgcolor: "#e5e7fc",
                color: "#4d5bec",
                fontWeight: 600,
                letterSpacing: "normal",
                py: 1.3,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#4d5bec",
                  color: "#fff",
                },
              }}
            >
              {status === "calcomSubmitting"
                ? i18n.t("oneClick.submitLoading")
                : i18n.t("oneClick.calcomCta")}
            </Button>
          )}
          {status === "error" && (
            <Typography sx={{ mt: 1.5, color: "#c00" }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default HubSpotForm;
